import { LocationDto } from "@apiModels/LocationDto.csharp";
import { SpecializationDto } from "@apiModels/SpecializationDto.csharp";
import { InsuranceServiceDto } from "@apiModels/Insurance/InsuranceServiceDto.csharp";
import { InsuranceDoctorDto } from "@apiModels/Insurance/InsuranceDoctorDto.csharp";
import { InsuranceKeyword } from "@apiModels/Insurance/InsuranceKeyword.csharp";

export type UrlQueryEntryName =
  | "specializationId"
  | "serviceId"
  | "locationIds"
  | "centerIds";

type QueryParam = { name: string; value: string | string[] };
type Context = {
  specialization?: SpecializationDto | null;
  locations?: LocationDto[] | null;
  service?: InsuranceServiceDto | null;
  doctor?: InsuranceDoctorDto | null;
  keywords?: InsuranceKeyword[] | null;
};

class UrlHandlerBase {
  static toUrl = (url: string) => {
    return new URL(
      url,
      `${location.protocol}//${location.hostname}${
        location.port ? `:${location.port}` : ""
      }`
    );
  };

  public static getQueryParametersFromUrl = (url: string): QueryParam[] => {
    const result = [] as QueryParam[];

    const params = this.toUrl(url).searchParams;
    params.forEach((value, key) => result.push({ name: key, value }));
    return result;
  };

  public static getQueryParametersFromCurrentUrl = () =>
    this.getQueryParametersFromUrl(window.location.href);

  public static changeQueryParametersInActiveUrl = (
    queryParameters: QueryParam[],
    addHistoryEntry = true
  ) => {
    const urlWithSearchParams = this.buildUrlWithQueryParameters(
      window.location.pathname,
      queryParameters
    );
    if (addHistoryEntry) {
      document.URL !== urlWithSearchParams &&
        window.history.pushState("", "", urlWithSearchParams);
    } else {
      window.history.replaceState("", "", urlWithSearchParams);
    }
  };

  public static buildUrlWithQueryParameters = (
    baseUrl: string,
    queryParams: QueryParam[]
  ) => {
    if (queryParams.length === 0) {
      return baseUrl;
    }

    const newUrl = this.toUrl(baseUrl);
    newUrl.search = "";

    queryParams.map((param: QueryParam) => {
      if (typeof param.value === "string") {
        newUrl.searchParams.append(param.name, param.value);
        return;
      }

      param.value.forEach((paramValue) =>
        newUrl.searchParams.append(param.name, paramValue)
      );
    });

    return newUrl.toString();
  };
}

export class UrlHandler extends UrlHandlerBase {
  public static adjustActiveUrl = (context: Context): void => {
    const { specialization, service, locations, doctor } = context;

    const parameters = {
      specializationId: specialization?.id,
      serviceId: service?.id,
      locationIds: locations
        ?.filter((selectedLocation) => selectedLocation.isLocation)
        .map((selectedLocation) => selectedLocation.id),
      centerIds: locations
        ?.filter((selectedLocation) => !selectedLocation.isLocation)
        .map((selectedLocation) => selectedLocation.id),
      doctorId: doctor?.id,
    };

    const parametersToQueryParameters = Object.entries(parameters)
      .map(([key, value]) => ({
        name: key,
        value,
      }))
      .filter((entry) => entry.value) as unknown as QueryParam[];

    this.changeQueryParametersInActiveUrl(parametersToQueryParameters, false);
  };

  public static getEntryFromUrlQuery = <T extends { id: string }>(
    list: T[],
    parameter: UrlQueryEntryName
  ): T | T[] | undefined => {
    const currentQueryParameters = this.getQueryParametersFromCurrentUrl();
    const queryItems = currentQueryParameters
      .filter((entry) => entry.name === parameter)
      .map((entry) => entry.value);

    if (queryItems.length === 1) {
      const [queryItem] = queryItems;
      const foundItem = list.find((item: T) => item.id === queryItem);
      return foundItem;
    }

    if (queryItems.length > 1) {
      return list.filter((item: T) => queryItems.includes(item.id));
    }

    return undefined;
  };
}
